/**
 * State machine driving Denker through cursor-led permissions and the
 * current desktop onboarding placeholder.
 *
 * Owns cursor position, mode, current step, bubble text, and per-permission
 * action-surface state.
 *
 * Spec: docs/specs/2026-04-30-desktop-onboarding-design.md §3.2 + §3.8
 *       docs/specs/2026-05-04-cursor-led-permissions-design.md
 */
import { create } from "zustand";
import {
  FLY_DURATION_MS,
  type ControllerState,
  type CursorAnim,
  type CursorMode,
  type Position,
  type StepId,
} from "./denker-cursor-controller-types";
import type { SceneId } from "./scenes/types";
import { type PermissionState } from "./permissions-wizard-state";
import { useOnboardingStore } from "@/stores/onboarding-store";

// ---------------------------------------------------------------------------
// Intro gate (backed by useOnboardingStore for cross-webview sync)
// ---------------------------------------------------------------------------

export function isIntroSeen(): boolean {
  return useOnboardingStore.getState().introSeen;
}

export function markIntroSeen(): void {
  useOnboardingStore.getState().markIntroSeen();
}

// Controller-wide epoch. Incremented by cancelAllScenes() (in the engine)
// so legacy epoch-alive guards (if any remain) bail on the next await.
// The engine is the sole caller via bumpControllerEpoch.
export const controllerEpochRef = { value: 0 };
export function bumpControllerEpoch(): void {
  controllerEpochRef.value += 1;
}
// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const INITIAL: ControllerState = {
  step: "arrive",
  walkerActive: false,
  position: { x: 0, y: 0 },
  cursorDisplayId: null,
  mode: "hidden",
  cursorAnim: null,
  cursorOpacity: 1,
  bubbleText: null,
  bubbleSelfDrive: false,
  bubbleHint: null,
  permState: { kind: "idle" } as PermissionState,
  currentSceneId: null,
};

// ---------------------------------------------------------------------------
// Actions interface
// ---------------------------------------------------------------------------

interface SetBubbleOpts {
  selfDrive?: boolean;
  hint?: string | null;
}

interface Actions {
  reset(): void;
  setStep(step: StepId): void;
  flyTo(target: Position): void;
  parkAt(target: Position): void;
  setBubble(text: string | null, opts?: SetBubbleOpts): void;
  setMode(mode: CursorMode): void;
  setAnim(anim: CursorAnim): void;
  setPermState(state: PermissionState): void;
  setCurrentSceneId(id: SceneId | null): void;

  // Cursor-led permission phases.
  runIntro(): Promise<void>;
  runPermHotkeys(): Promise<void>;
  runPermVoice(): Promise<void>;
  runPermVision(): Promise<void>;

  // Terminal all-set scene.
  beginIntake(): Promise<void>;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useDenkerController = create<ControllerState & Actions>((set) => ({
  ...INITIAL,

  reset: () => set({ ...INITIAL }),

  setStep: (step) => set({ step }),

  flyTo: (target) => {
    set({ mode: "flying", position: target });
    setTimeout(
      () =>
        set((s) => ({ ...s, mode: s.mode === "flying" ? "parked" : s.mode })),
      FLY_DURATION_MS + 50,
    );
  },

  parkAt: (target) =>
    set({ mode: "parked", position: target, cursorDisplayId: null }),

  setBubble: (text, opts) => {
    set({
      bubbleText: text,
      bubbleSelfDrive: opts?.selfDrive ?? false,
      bubbleHint: opts?.hint ?? null,
    });
    // Narration is hooked off the bubbleText *state change* in
    // `attachBubbleNarrator()` in controller-lifecycle, not off this action.
    // way, monitor-handoff remounts that re-issue setBubble with the
    // current text don't re-trigger speech — the broadcaster's
    // serialized-key dedup already drops no-op state mutations.
  },

  setMode: (mode) =>
    set({
      mode,
      ...(mode === "parked" ? { cursorDisplayId: null } : {}),
    }),

  setAnim: (anim) => {
    set({ cursorAnim: anim });
    if (anim !== null) {
      // Auto-reset after the CSS animation completes (~700ms).
      setTimeout(() => {
        set((s) => (s.cursorAnim === anim ? { ...s, cursorAnim: null } : s));
      }, 750);
    }
  },

  setPermState: (state) => set({ permState: state }),

  setCurrentSceneId: (id) => set({ currentSceneId: id }),

  // -------------------------------------------------------------------------
  // Intro + permission phases (cursor-led)
  // -------------------------------------------------------------------------

  runIntro: async () => {
    const { runDriver } = await import("./scene-engine");
    await runDriver("intro");
  },

  runPermHotkeys: async () => {
    set({ permState: { kind: "idle" } });
    const { runDriver } = await import("./scene-engine");
    await runDriver("permHotkeys");
  },

  runPermVoice: async () => {
    set({ permState: { kind: "idle" } });
    const { runDriver } = await import("./scene-engine");
    await runDriver("permVoice");
  },

  runPermVision: async () => {
    set({ permState: { kind: "idle" } });
    const { runDriver } = await import("./scene-engine");
    await runDriver("permVision");
  },

  beginIntake: async () => {
    const { runDriver } = await import("./scene-engine");
    await runDriver("intake");
  },
}));

export function narrationHold(text: string, minMs = 3600): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const revealMs = text.length * 30 + 900;
  return Math.max(minMs, words * 430, revealMs);
}
